import { get } from "lodash";
import * as React from "react";

const { Component } = React;

import { Button, FormGroup, Input, Label } from "../components/Modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as AccountStore from "../account/AccountStore";
import { GetContext, UnsubscribeContext } from "../general/ApplicationContext";
import Loader from "../general/Loader";
import ManagedServiceInstance from "../managedServices/ManagedInstance";
import * as ManagedServiceStore from "../managedServices/ManagedServiceStore";
import { AzureManagedService, RenderAzureRow } from "../managedServices/services/Azure";
import { DiscordManagedService, RenderDiscordRow } from "../managedServices/services/Discord";
import { LinkManagedService, RenderLinkRow } from "../managedServices/services/Link";
import { RenderSASAMSRow, SASAMSManagedService } from "../managedServices/services/SASAMS";
import { RenderScadsysRow, ScadsysManagedService } from "../managedServices/services/Scadsys";
import { RenderScadsysBiometricsRow, ScadsysBiometricsManagedService } from "../managedServices/services/ScadsysBiometrics";
import { RenderSendGridRow, SendGridManagedService } from "../managedServices/services/SendGrid";
import { RenderStoreRow, StoreManagedService } from "../managedServices/services/Store";
import { RenderSage300Row, Sage300ManagedService } from "../managedServices/services/Sage300";
import { RenderScadsysAssetsRow, ScadsysAssetsManagedService } from "../managedServices/services/ScadsysAssets";

import { AppWebsiteManagedService, RenderAppWebsiteRow } from "../managedServices/services/AppWebsite";

interface OrganizationManagedServicesState {
    organization: string;

    showModal: boolean;
    currentUser: any;

    isLoading: boolean;

    managedServices: Array<any>;

    currentManagedService: any;
}

interface OrganizationManagedServicesProps {}

let contextSubscriptionId = "";

export var ConfigFormField = (key, name, state, handleChange) => {
    return (
        <FormGroup>
            <Label htmlFor={key}>{name}</Label>
            <Input id={key} type="text" value={get(state, key)} onChange={handleChange(key)} />
        </FormGroup>
    );
};

const defaultServices = {
    managedServices: [],
    currentManagedService: null,
};

export default class OrganizationManagedServices extends Component<OrganizationManagedServicesProps, OrganizationManagedServicesState> {
    constructor(props) {
        super(props);
        this.state = {
            organization: "",
            showModal: false,
            currentUser: undefined,
            isLoading: true,
            ...defaultServices,
        };
    }

    componentDidMount() {
        contextSubscriptionId = GetContext((context) => {
            this.setState({ organization: context.currentOrganization.id, ...defaultServices }, () => {
                AccountStore.GetCurrentLoggedInUser().then((user) => {
                    this.setState({ currentUser: user });
                });

                this.listManagedServices();
            });
        });
    }

    listManagedServices = () => {
        ManagedServiceStore.ListManagedServices(this.state.organization).then((response) => {
            this.setState({ managedServices: response.data, isLoading: false, currentManagedService: null });
        });
    };

    componentWillUnmount() {
        UnsubscribeContext(contextSubscriptionId);
    }

    showManagedServiceInstace = (service) => () => {
        this.setState({ currentManagedService: service });
    };

    render() {
        return (
            <div className="space-y-6">
                {/* Header Section */}
                <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
                    <FontAwesomeIcon icon={["fad", "plug"]} className="text-3xl text-purple-500" />
                    <div>
                        <h2 className="text-2xl font-semibold text-gray-800">Managed Services</h2>
                        <p className="text-sm text-gray-600">Configure and manage external service integrations</p>
                    </div>
                </div>

                {this.state.isLoading && <Loader />}

                {!this.state.isLoading && (
                    <>
                        {/* Back Button when viewing a service instance */}
                        {this.state.currentManagedService && (
                            <Button variant="secondary" onClick={() => this.setState({ currentManagedService: null })}>
                                <FontAwesomeIcon icon={["fad", "arrow-left"]} className="mr-2" />
                                Back to Services
                            </Button>
                        )}

                        {/* Service Instance View */}
                        {this.state.currentManagedService ? (
                            <div className="bg-white rounded-lg shadow-sm p-6">
                                <ManagedServiceInstance managedService={this.state.currentManagedService} onChange={this.listManagedServices} />
                            </div>
                        ) : (
                            <>
                                {/* Available Services Grid */}
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                        <FontAwesomeIcon icon={["fad", "plus-circle"]} className="text-green-500" />
                                        Available Services
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        {this.state.managedServices.filter((m) => m.type == 1).length == 0 && (
                                            <SASAMSManagedService organizationId={this.state.organization} onDone={() => this.listManagedServices()} />
                                        )}
                                        {this.state.managedServices.filter((m) => m.type == 2).length == 0 && (
                                            <ScadsysManagedService organizationId={this.state.organization} onDone={() => this.listManagedServices()} />
                                        )}
                                        {this.state.managedServices.filter((m) => m.type == 4).length == 0 && (
                                            <ScadsysBiometricsManagedService organizationId={this.state.organization} onDone={() => this.listManagedServices()} />
                                        )}
                                        <AppWebsiteManagedService organizationId={this.state.organization} onDone={() => this.listManagedServices()} />
                                        <StoreManagedService organizationId={this.state.organization} onDone={() => this.listManagedServices()} />
                                        <AzureManagedService organizationId={this.state.organization} onDone={() => this.listManagedServices()} />
                                        <SendGridManagedService organizationId={this.state.organization} onDone={() => this.listManagedServices()} />
                                        <LinkManagedService organizationId={this.state.organization} onDone={() => this.listManagedServices()} />
                                        <DiscordManagedService organizationId={this.state.organization} onDone={() => this.listManagedServices()} />
                                        <Sage300ManagedService organizationId={this.state.organization} onDone={() => this.listManagedServices()} />
                                        {this.state.managedServices.filter((m) => m.type == ManagedServiceStore.ManagedServiceType.ScadsysAssets).length == 0 && (
                                            <ScadsysAssetsManagedService organizationId={this.state.organization} onDone={() => this.listManagedServices()} />
                                        )}
                                    </div>
                                </div>

                                {/* Configured Services Table */}
                                {this.state.managedServices.length > 0 && (
                                    <div className="mt-8">
                                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                                            <FontAwesomeIcon icon={["fad", "check-circle"]} className="text-green-500" />
                                            Configured Services
                                            <span className="ml-2 px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-700">{this.state.managedServices.length}</span>
                                        </h3>
                                        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                                            <table className="w-full">
                                                <thead className="bg-gray-50 border-b border-gray-200">
                                                    <tr>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service Type</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Configuration</th>
                                                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-200">
                                                    {this.state.managedServices.map((service, i) => (
                                                        <tr key={i} className="hover:bg-gray-50 transition-colors">
                                                            <td className="px-6 py-4">
                                                                <div className="flex items-center gap-3">
                                                                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white">
                                                                        <FontAwesomeIcon icon={["fad", "cog"]} />
                                                                    </div>
                                                                    <div>
                                                                        <div className="font-medium text-gray-900">{ManagedServiceStore.TypeName(service.type)}</div>
                                                                        <div className="text-xs text-gray-500">Type {service.type}</div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-4 text-sm text-gray-600">
                                                                {service.type == 11 && RenderAppWebsiteRow(service.configuration)}
                                                                {service.type == 0 && RenderLinkRow(service.configuration)}
                                                                {service.type == 1 && RenderSASAMSRow(service.configuration)}
                                                                {service.type == 2 && RenderScadsysRow(service.configuration)}
                                                                {service.type == 3 && RenderStoreRow(service.configuration)}
                                                                {service.type == 4 && RenderScadsysBiometricsRow(service.configuration)}
                                                                {service.type == 5 && RenderAzureRow(service.configuration)}
                                                                {service.type == 7 && RenderSendGridRow(service.configuration)}
                                                                {service.type == 8 && RenderDiscordRow(service.configuration)}
                                                                {service.type == 9 && RenderSage300Row(service.configuration)}
                                                                {service.type == 10 && RenderScadsysAssetsRow(service.configuration)}
                                                            </td>
                                                            <td className="px-6 py-4 text-right">
                                                                <Button variant="primary" onClick={this.showManagedServiceInstace(service)}>
                                                                    <FontAwesomeIcon icon={["fad", "eye"]} className="mr-2" />
                                                                    View Details
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {/* Empty State */}
                                {this.state.managedServices.length === 0 && (
                                    <div className="bg-white rounded-lg shadow-sm p-12 text-center">
                                        <FontAwesomeIcon icon={["fad", "plug"]} className="text-6xl text-gray-300 mb-4" />
                                        <h3 className="text-lg font-semibold text-gray-800 mb-2">No Services Configured</h3>
                                        <p className="text-gray-600">Get started by configuring your first managed service above</p>
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}
            </div>
        );
    }
}
